import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFolders } from 'common/api/folder';
import qs from 'qs';
import { createRun, updateRun } from 'common/api/run';
import { TOASTIFY_TYPES, reactToastify } from 'common/utils/reactToastify';
import { initiateUserAuth } from 'common/auth/modalLogin';
import moment from 'moment';
import { quotaVerificationDom } from 'ui/QuotaVerification/quotaDom';
import { createMessage, sendMessageToParent, types } from 'common/utils/iframe';
import { RunConfigSanitizer } from '../sanitizer/index';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const useRunSubmit = () => {
  const runConfig = useSelector((state) => state);
  const [isLoading, setIsLoading] = useState(false);

  const isPremiumOrProFeaturesEnabled = useMemo(() => {
    if (!runConfig) {
      return false;
    }
    const cases = _.get(runConfig, 'quantConfig.cases', []);
    const expiryHandling = _.get(runConfig, 'quantConfig.bounds.expiryHandling', {});
    const isMultipleCaseEnabled = _.size(cases) > 1;
    const isCalenderSpreadEnabled = _.get(expiryHandling, 'legacyClose', false);
    const isAdjustmentAdded = cases?.some(c => Array.isArray(c?.adjustments) && c?.adjustments?.length > 0);

    const isStrikeMultiplierEnabled = cases?.some(c =>
    (c?.entry?.intents?.some(i => (
      i?.instrument?.strike?.multiplier > 0 ||
      i?.instrument?.type === 'IndicatorValueBasedStrikeIndex' ||
      _.size(i?.uiConfigs?.quickAdjustments) > 0 ||
      _.size(_.get(i, 'adjustments')) > 0
    )) || false)
    );

    const isWeeklyExpiryEnabled = cases?.some(c =>
      _.size(c?.entry?.intents) > 1 && c?.entry?.intents?.some(i => (
        i?.instrument?.expiry?.index >= 2
      ))

    );

    const isAdvancedOptionsEnabled = _.get(runConfig, 'quantConfig.uiConfigs.mode', '') === 'advanced';

    return isMultipleCaseEnabled ||
      isStrikeMultiplierEnabled ||
      isAdvancedOptionsEnabled ||
      isWeeklyExpiryEnabled ||
      isCalenderSpreadEnabled ||
      isAdjustmentAdded;
  }, [runConfig]);

  const {
    runType,
    granularity,
    quantConfig,
    startDate,
    selectedFolder,
    endDate,
    strategyId,
    runName,
    orderType,
    engineVersion,
    isEditQuant,
    hiddenByUserId,
    isHiddenSignal,
    shouldEmitOnSubmit,
    isStrategyAuthor
  } = runConfig;

  const { instrumentGroups } = quantConfig;

  const [isUserLoggedIn, setIsUserLoggedIn] = useState(window?.isUserSignedIn);

  const getDefaultFolderId = useCallback(async () => {
    const defaultFolderId = await getFolders().then((folders) => {
      const { id } = _.find(folders, 'is_default');
      return id;
    });

    return defaultFolderId;
  }, []);

  const getSanitizedRunParams = () => {
    const folderId = _.get(selectedFolder, 'id', '') || runConfig?.folderId || null;
    const runParams = {
      name: runName,
      engine_version: engineVersion || 2,
      folder_id: folderId,
      quant_config: quantConfig,
      run_type: runType,
      order_type: orderType,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      is_webhook_enabled: false,
      strategy_id: strategyId || null,
      granularity,
      is_strategy_author: isStrategyAuthor
    };

    if (isHiddenSignal) {
      runParams.hidden_by_user_id = hiddenByUserId;
    }

    return RunConfigSanitizer.beforeSubmit(runParams);
  };

  const postRunToParent = () => {
    const message = createMessage(types.RUN, getSanitizedRunParams());
    sendMessageToParent(message);
  };

  const executeEditRun = (runParams) => {
    const queryString = window.location.search;
    const parsedQueryString = qs.parse(queryString, { parseBooleans: true });
    const runId = _.get(parsedQueryString, 'edit_run_id');
    updateRun({ runId, run: { ...runParams } })
      .then(() => {
        window.location.href = `/runs/${runId}`;
      })
      .catch((error) => {
        try {
          error.json().then((e) => {
            const errors = _.get(e, 'errors', []);
            const errorMessages = _.join(_.get(_.values(errors), '0', []), ',');
            setIsLoading(false);
            if (errorMessages) {
              reactToastify(errorMessages, TOASTIFY_TYPES.ERROR);
            }
          });
        } catch {
          setIsLoading(false);
          reactToastify(
            'Something went wrong, unable to edit run.',
            TOASTIFY_TYPES.ERROR
          );
        }
      });
    return null;
  };

  const executeCreateRun = (runParams) => {
    createRun(runParams).catch((error) => {
      const errors = _.get(error.response, 'data.errors', []);
      const additionalErrors = _.filter(
        errors,
        (err, key) => key !== 'quant_config'
      );
      const errorMessages = _.join(_.map(additionalErrors, '0'), ', ');
      setIsLoading(false);
      if (errorMessages) reactToastify(errorMessages, TOASTIFY_TYPES.ERROR);
    });

    return null;
  };

  const handleCreateRun = async () => {
    try {
      const sanitizedRunParams = getSanitizedRunParams();
      if (!sanitizedRunParams.folder_id) {
        const defaultFolderId = await getDefaultFolderId();
        sanitizedRunParams.folder_id = defaultFolderId;
      }

      if (isEditQuant) {
        return executeEditRun(sanitizedRunParams);
      }
      return quotaVerificationDom(
        instrumentGroups,
        sanitizedRunParams,
        executeCreateRun
      );
    } catch (error) {
      console.error(error);
      reactToastify(
        'Something went wrong, Please try again',
        TOASTIFY_TYPES.ERROR
      );

      return setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    if (shouldEmitOnSubmit) {
      return postRunToParent();
    }

    if (!isUserLoggedIn) {
      return initiateUserAuth()
        .then(() => {
          setIsUserLoggedIn(true);
          handleCreateRun();
          setIsLoading(true);
        })
        .catch(() => {
          reactToastify(
            'Unauthorized, Please Login and continue',
            TOASTIFY_TYPES.ERROR
          );
        });
    }

    setIsLoading(true);
    return handleCreateRun();
  };


  const shouldShowAlgoverseCostMessage = useMemo(() => {
    return ApplicationDomainUtility.isAlgoverse && runType === 'live';
  }, [runType]);

  return {
    onSubmit,
    isLoading,
    shouldShowAlgoverseCostMessage,
    isPremiumOrProFeaturesEnabled,
    isLive: runType === 'live',
  };
};

export default useRunSubmit;
